import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import TeamColourDot from 'components/admin/TeamColourDot';
import routes from 'routes';
import { formatPoints } from 'utils/points';
import { convertDateTime } from 'utils/time';
import styles from './HousePoints.module.css';

// The House Points report's table (docs/school-carnivals-points-slice-5.md in the portal repo): each house's
// points over the active season. The rows are the server's (SeasonHousePointsDto.houses), best first: the
// house's place (equal totals share one, by the club's ties rule; none for a total of 0 or below, shown as –),
// its total at each meet that counts for season points (the number on that meet's House Totals card), the
// season total, its athletes who took part at any of the meets, and the total divided by them. A meet's name
// links to its Points page, which has the breakdown. The card, header colour and place badges are the House
// Totals card's, so the two read alike.

const placeBadgeClass = (place) => {
  if (place === 1) return styles.placeGold;
  if (place === 2) return styles.placeSilver;
  if (place === 3) return styles.placeBronze;
  return styles.placeDefault;
};

const HousePoints = ({ clubId, meets, houses, teamWords }) => (
  <div className={styles.card}>
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.centered}>Place</th>
            <th>{teamWords.team}</th>
            {meets.map((meet) => (
              <th key={meet.meetId} className={styles.number}>
                <Link className={styles.meetLink} to={routes.admin.meetPointsDashboard.url(clubId, meet.meetId)}>
                  {meet.meetName}
                </Link>
                <span className={styles.meetDate}>{convertDateTime(meet.meetDate).date}</span>
              </th>
            ))}
            <th className={styles.number}>Total</th>
            <th className={styles.number}>Athletes</th>
            <th className={styles.number}>Average</th>
          </tr>
        </thead>
        <tbody>
          {houses.map((house) => (
            <tr key={house.teamId}>
              <td className={styles.centered}>
                {house.place ? (
                  <span className={`${styles.placeBadge} ${placeBadgeClass(house.place)}`}>{house.place}</span>
                ) : (
                  '–'
                )}
              </td>
              <td className={styles.name}>
                <span className={styles.nameInner}>
                  <TeamColourDot colour={house.colour} />
                  {house.name}
                </span>
              </td>
              {meets.map((meet, i) => (
                <td key={meet.meetId} className={styles.number}>
                  {formatPoints(house.meetPoints[i] ?? 0)}
                </td>
              ))}
              <td className={`${styles.number} ${styles.total}`}>{formatPoints(house.totalPoints)}</td>
              <td className={styles.number}>{house.athletes}</td>
              <td className={styles.number}>
                {house.averagePoints === null || house.averagePoints === undefined
                  ? '–'
                  : formatPoints(house.averagePoints)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

HousePoints.propTypes = {
  // The club in the Points pages' addresses.
  clubId: PropTypes.string.isRequired,
  // The meets that count for season points, in date order: the columns.
  meets: PropTypes.arrayOf(
    PropTypes.shape({
      meetId: PropTypes.string,
      meetName: PropTypes.string,
      meetDate: PropTypes.string,
    })
  ).isRequired,
  houses: PropTypes.arrayOf(
    PropTypes.shape({
      teamId: PropTypes.string,
      name: PropTypes.string,
      colour: PropTypes.string,
      place: PropTypes.number,
      // The house's total at each meet, in the order of meets.
      meetPoints: PropTypes.arrayOf(PropTypes.number),
      totalPoints: PropTypes.number,
      athletes: PropTypes.number,
      averagePoints: PropTypes.number,
    })
  ).isRequired,
  // The school's words for a house (utils/teamLabel.js).
  teamWords: PropTypes.shape({ team: PropTypes.string }).isRequired,
};

export default HousePoints;
